import React, { ComponentType, Fragment } from 'react'
import uniqid from 'uniqid'
import _ from 'lodash'

import { FormatDate } from '../utils'
import { withScroller } from '../utils/withScroller'

// TODO: Move these types up a level so that they can be shared between related components
export type DataViewEntityAnalyst = {
  firstName: string
  lastName: string
  role: string
  sequenceNumber: number
  type: string
}

export type DataViewEntityMarketingItem = {
  name: string
  slug: string
}

export type DataViewEntityMarketing = {
  analysts: DataViewEntityAnalyst[] | null
  countries: DataViewEntityMarketingItem[]
  sectors: DataViewEntityMarketingItem[]
}

export type DataViewEntityRating = {
  orangeDisplay: boolean
  ratingActionDescription: string
  ratingAlertCode: string | null
  ratingAlertDescription: string
  ratingCode: string
  ratingEffectiveDate: string
  ratingTypeDescription: string
  sequenceNumber?: number
  correctionFlag: boolean
  ratingChangeDate: string
}

export type DataViewEntityItem = {
  name: string
  ultimateParent?: boolean
  permalink: string
  marketing: DataViewEntityMarketing
  ratings: DataViewEntityRating[]
  renderLink?: ComponentType<DataViewEntityRenderLinkProps>
}

export type DataViewEntityRenderLinkProps = Partial<DataViewEntityItem>

export type DataViewEntityProps = {
  items: DataViewEntityItem[]
  renderLink?: ComponentType<DataViewEntityRenderLinkProps>
  useScroller?: boolean
}

const renderLinkDefault = ({
  name,
  permalink
}: DataViewEntityRenderLinkProps) => <a href={permalink}>{name}</a>

const sortAnalysts = (analysts: DataViewEntityAnalyst[]) => {
  analysts.sort((a: DataViewEntityAnalyst, b: DataViewEntityAnalyst) => {
    if (a.sequenceNumber < b.sequenceNumber) return -1
    if (a.sequenceNumber > b.sequenceNumber) return 1
    return 0
  })

  return analysts
}

const getRatingsTable = (ratings: DataViewEntityRating[]) =>
  ratings && ratings.length ? (
    <table className="table__wrapper entity-data">
      <colgroup>
        <col width="26.66%" />
        <col width="14.35%" />
        <col width="15.89%" />
        <col width="36.41%" />
      </colgroup>

      <tbody>
        {ratings
          ? ratings.map((rating: DataViewEntityRating) => (
              <tr key={uniqid()}>
                <td className={rating.orangeDisplay ? 'eu-rating-text' : ''}>
                  {rating.ratingCode}
                  <span
                    className={
                      rating.ratingAlertCode
                        ? rating.ratingAlertCode.replace(':', '')
                        : ''
                    }
                  />
                  {rating.correctionFlag ? (
                    <>
                      <br /> Rating Correction <br />
                      {`(${FormatDate(
                        rating.ratingChangeDate,
                        'dd-MMM-yyyy'
                      )})`}
                      <br />
                    </>
                  ) : null}
                </td>
                <td>{rating.ratingActionDescription}</td>
                <td>{FormatDate(rating.ratingEffectiveDate, 'dd-MMM-yyyy')}</td>
                <td className="cell--wrappable">
                  {rating.ratingTypeDescription}
                </td>
              </tr>
            ))
          : null}
      </tbody>
    </table>
  ) : null
const renderItem = (item: DataViewEntityItem, useScroller: boolean) => {
  const {
    name,
    ultimateParent,
    permalink,
    marketing,
    ratings,
    renderLink: Link = renderLinkDefault
  } = item
  const { analysts, countries, sectors } = marketing || {
    analysts: [],
    countries: [],
    sectors: []
  }
  const linkProps = { name, permalink }

  const ratingsTable = getRatingsTable(ratings)

  return (
    <div key={permalink} className="column__four column--merge-a entity-data">
      <div className="column entity-data--title">
        {ultimateParent ? (
          <div className="heading--sub heading--tag">Ultimate Parent</div>
        ) : null}

        <h3 className="heading--5">
          <Link {...linkProps} />
        </h3>
      </div>
      <div className="column entity-data--ratings">
        {ratingsTable ? (
          <div className="table table--1">
            {useScroller ? withScroller(ratingsTable) : ratingsTable}
          </div>
        ) : (
          <p>-</p>
        )}
      </div>
      <div className="column entity-data--sector">
        <p>
          {sectors.map((sector: DataViewEntityMarketingItem) => (
            <Fragment key={sector.name}>
              {sector.name}
              <br />
            </Fragment>
          ))}
          <br />
          {countries
            ? countries.map((country: DataViewEntityMarketingItem) => (
                <Fragment key={country.name}>
                  {country.name}
                  <br />
                </Fragment>
              ))
            : null}
        </p>
      </div>

      <div className="column entity-data--analyst">
        {analysts
          ? sortAnalysts(analysts).map((analyst: DataViewEntityAnalyst) => (
              <Fragment key={analyst.firstName.concat(analyst.lastName)}>
                <p>
                  {_.startCase(analyst.lastName)},{' '}
                  {_.startCase(analyst.firstName)}
                  <br />
                  {analyst.type}
                </p>
              </Fragment>
            ))
          : null}
      </div>
    </div>
  )
}

export const DataViewEntity = ({
  items,
  useScroller = true
}: DataViewEntityProps) => (
  <div className="column__main">
    <div className="column__four column--merge-a  entity-data">
      <div className="column entity-data--title">
        <h6 className="heading--6">Entity</h6>
      </div>
      <div className="column entity-data--ratings">
        <h6 className="heading--6">Rating</h6>
      </div>
      <div className="column entity-data--sector">
        <h6 className="heading--6">Sector &amp; Country</h6>
      </div>
      <div className="column entity-data--analyst">
        <h6 className="heading--6">Analyst(s)</h6>
      </div>
    </div>
    {items
      ? items.map((item: DataViewEntityItem) => renderItem(item, useScroller))
      : null}
  </div>
)
